import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBuilding } from "../context/buildingContext";
import { fetchApi } from "../utils/fetchApi";
import { useNavigate } from "react-router-dom";
import { useUser } from '../context/userContext';
import { displayFloorAndDoorByName, sortByApartmentName } from '../utils/helpers';

interface Building {
  id: number;
  country: string;
  postalCode: number;
  city: string;
  street: string;
}

interface Apartment {
  apartment_id: number;
  name: string;
  rights: number;
}

const BuildingSelector: React.FC = () => {
  const { buildingId, setBuildingData } = useBuilding();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useUser();

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>(buildingId);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [currentApartment, setCurrentApartment] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Épületek betöltése API-ból
  useEffect(() => {
    const loadBuildings = async () => {
      try {
        const response: [number, number, string, number, string, string, number, number][] =
          await fetchApi("get_building_access_rights");

        const formattedBuildings: Building[] = response.map(([id, , country, postalCode, city, street]) => ({
          id,
          country,
          postalCode,
          city,
          street,
        }));

        setBuildings(formattedBuildings);
        if (formattedBuildings.length === 1) {
          setCurrentBuildingId(formattedBuildings[0].id.toString())
        }
      } catch (err) {
        console.error("Error loading buildings:", err);
        setError(t("buildingSelector.error.buildingLoad"));
      } finally {
        setLoading(false);
      }
    };

    loadBuildings();
  }, [t]);

  // Apartmanok betöltése API-ból az adott épület alapján
  useEffect(() => {
    const loadApartments = async () => {
      if (currentBuildingId) {
        try {
          const response: [number, number, string][] = await fetchApi("get_apartment_access_rights", {
            building: currentBuildingId,
          });

          const formattedApartments: Apartment[] = response.map(([apartment_id, rights, name]) => ({
            apartment_id,
            rights,
            name,
          }));
          setApartments(sortByApartmentName(formattedApartments));
          if (formattedApartments.length === 1) {
            const currentApartmentId = formattedApartments[0].apartment_id.toString()
            setCurrentApartment(currentApartmentId);
            if (buildingId) {
              redirectToDashboardWithContext(buildingId, currentApartmentId, formattedApartments[0].name);
            }
          }
        } catch (err) {
          console.error("Error loading apartments:", err);
          setError(t("buildingSelector.error.apartmentLoad"));
        }
      }
    };

    loadApartments();
  }, [currentBuildingId, t]);

  const handleBuildingChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedBuilding = event.target.value;
    setCurrentBuildingId(selectedBuilding);
    setCurrentApartment(null);
  };

  const handleApartmentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentApartment(event.target.value);
  };

  const getSelectedApartmentData = (apartmentId: string) => (
    apartments.find((apt) => apt.apartment_id.toString() === apartmentId)
  )

  const redirectToDashboardWithContext = (buildingId: string, apartmentId: string, name?: string) => {
    if (buildingId && apartmentId) {
      const selectedApartment = getSelectedApartmentData(apartmentId)
      setBuildingData(buildingId, apartmentId, name ?? selectedApartment?.name ?? "");
      setError(null);
      navigate("/dashboard");
    }
  }

  const handleSave = () => {
    if (currentBuildingId && currentApartment) {
      redirectToDashboardWithContext(currentBuildingId, currentApartment);
    } else {
      setError(t("buildingSelector.error.selectBoth"));
    }
  };

  return (
    <div className="buildingSelectorContainer">
      {loading ? (
        <p>{t("loading")}</p>
      ) : error ? (
          <>
            <p className="error">{error}</p>
            <button className="logoutButton" onClick={logout}>
              {t('logOut')}
            </button>
          </>
      ) : (
        <>
          <div className="selector">
            <label htmlFor="building">{t("buildingSelector.building")}</label>
            <select id="building" value={currentBuildingId || ""} onChange={handleBuildingChange}>
              <option value="" disabled>
                {t("buildingSelector.selectBuilding")}
              </option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id.toString()}>
                  {`${building.country} ${building.postalCode} ${building.city} ${building.street}`}
                </option>
              ))}
            </select>
          </div>

          {currentBuildingId && (
            <div className="selector">
              <label htmlFor="apartment">{t("buildingSelector.apartment")}</label>
              <select id="apartment" value={currentApartment || ""} onChange={handleApartmentChange}>
                <option value="" disabled>
                  {t("buildingSelector.selectApartment")}
                </option>
                {apartments.map((apartment) => (
                  <option key={apartment.apartment_id} value={apartment.apartment_id.toString()}>
                    {displayFloorAndDoorByName(apartment.name)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {error && <div className="error">{error}</div>}
          <button className="button" onClick={handleSave}>
            {t("buildingSelector.save")}
          </button>
        </>
      )}
    </div>
  );
};

export default BuildingSelector;
